import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, InputBaseComponentProps } from '@mui/material';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { NumberFormatProps } from 'react-number-format';

import { FormikTextField } from '../formik-text-field';
import { makeNumberFormatInput } from '../number-format-field';

type FormikTextFieldProps = React.ComponentProps<typeof FormikTextField>;
type Props = Omit<FormikTextFieldProps, 'type'> & {
  format?: NumberFormatProps<unknown>;
};

const FormikPasswordField = (props: Props): ReactElement => {
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const { InputProps, format, disabled, ...rest } = props;

  const numberFormatProps = useMemo(() => {
    const numberFormatInput = makeNumberFormatInput(format);
    return {
      inputComponent: numberFormatInput as React.ElementType<InputBaseComponentProps>,
    };
  }, [format]);

  const inputProps: typeof InputProps = useMemo(
    () => ({
      ...InputProps,
      ...(format ? numberFormatProps : []),
      endAdornment: (
        <InputAdornment position="end">
          <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} disabled={disabled}>
            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
        </InputAdornment>
      ),
    }),
    [InputProps, disabled, format, handleClickShowPassword, numberFormatProps, showPassword],
  );

  return (
    <>
      <FormikTextField
        {...rest}
        InputProps={inputProps}
        disabled={disabled}
        type={showPassword ? 'text' : 'password'}
      />
    </>
  );
};

export { FormikPasswordField };
